import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  INELIGIBLE_REASONS,
  REASON_LABELS,
  RECOVERY_REASONS,
  type RecoverableCheckoutLine,
  type RecoverableCheckoutsSummary,
  type RecoveryQuery,
  type RecoveryReason,
  type RecoveryTally,
  type RecoveryWeek,
} from '@/api/recovery'
import type { Environment } from '@/api/types'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import {
  Badge,
  type BadgeTone,
  Card,
  type Column,
  DataTable,
  EmptyState,
  formatCurrency,
  formatInteger,
  KpiCard,
  LoadingBlock,
  SegmentedControl,
} from '@/design'
import { usePermissions } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'
import { saveBlob } from '@/features/analytics/traffic/download'

import {
  PAGE_SIZE,
  WEEK_OPTIONS,
  type WeekOption,
  useRecoveryList,
  useRecoveryParams,
  useRecoverySummary,
} from './recoveryQuery'
import './abandonment.css'

/**
 * Analytics › Abandonment. Its first panel: recoverable checkouts (card 3e5c00e6) —
 * failed and cancelled orders, and how much of that a recovery email could reach.
 * The five-category abandonment table (its own card) joins it on this page.
 */
export function AbandonmentPage({ route }: { route: RouteDef }) {
  return (
    <SectionFrame route={route}>
      <RecoverableCheckouts />
    </SectionFrame>
  )
}

function money(minor: number, currency: string): string {
  return formatCurrency(minor / 100, { currency })
}

function weekLabel(weekStart: string): string {
  return new Date(`${weekStart}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

const REASON_TONES: Record<RecoveryReason, BadgeTone> = {
  eligible: 'positive',
  paid_since: 'info',
  competition_closed: 'neutral',
  competition_unknown: 'warning',
  guest: 'neutral',
  suppressed: 'negative',
  no_email_consent: 'warning',
}

function RecoverableCheckouts() {
  const { filters } = useGlobalFilters()
  const { weeks, setWeeks, reason, setReason } = useRecoveryParams()
  const summary = useRecoverySummary(weeks, filters.environments)
  const client = useApiClient()
  usePageFreshness(
    summary.data ? { source: client.kind === 'http' ? 'live' : 'demo', lastUpdatedAt: summary.data.generatedAt } : undefined,
  )

  return (
    <div className="ci-stack">
      <div className="rec-controls">
        <SegmentedControl<`${WeekOption}`>
          label="Weeks shown"
          options={WEEK_OPTIONS.map((option) => ({ value: `${option}`, label: `${option} weeks` }))}
          value={`${weeks}`}
          onChange={(value) => setWeeks(Number(value) as WeekOption)}
        />
      </div>
      <SummaryBody summary={summary} />
      {summary.data ? (
        <LinesCard
          weeks={weeks}
          environments={filters.environments}
          reason={reason}
          onReason={setReason}
          currency={summary.data.currency}
        />
      ) : null}
    </div>
  )
}

function SummaryBody({ summary }: { summary: ReturnType<typeof useRecoverySummary> }) {
  if (summary.isPending) return <LoadingBlock rows={4} label="Loading recoverable checkouts" />
  if (!summary.data) return <EmptyState variant="outage" announce />
  const data = summary.data
  const unfinished = data.totals.eligible.count + data.totals.ineligible.count
  if (unfinished === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No unfinished checkouts"
        description={`No failed or cancelled orders in the last ${data.weeks.length} weeks. They appear here once your website's orders are syncing.`}
      />
    )
  }
  const thisWeek = data.weeks[data.weeks.length - 1]
  return (
    <>
      <section className="rec-kpis" aria-label="Recoverable checkouts">
        <KpiCard
          label="Recoverable this week"
          value={money(thisWeek?.eligible.valueMinor ?? 0, data.currency)}
          change={null}
          polarity="neutral"
          note={`${formatInteger(thisWeek?.eligible.count ?? 0)} lines so far`}
        />
        <KpiCard
          label={`Recoverable, ${data.weeks.length} weeks`}
          value={money(data.totals.eligible.valueMinor, data.currency)}
          change={null}
          polarity="neutral"
          note={`${formatInteger(data.totals.eligible.count)} lines`}
        />
        <KpiCard
          label="Not recoverable"
          value={money(data.totals.ineligible.valueMinor, data.currency)}
          change={null}
          polarity="neutral"
          note={`${formatInteger(data.totals.ineligible.count)} lines`}
        />
        <KpiCard
          label="Unfinished checkouts"
          value={formatInteger(unfinished)}
          change={null}
          polarity="neutral"
          note="Failed or cancelled"
        />
      </section>
      <p className="rec-note">
        A failed or cancelled order counts once it has sat for {data.minAgeMinutes} minutes, one line per competition in
        it, and once per customer per competition. <strong>Recoverable</strong> means an email could reach them: they
        agreed to marketing email, are not unsubscribed, have not paid for it since, and the competition is still open.
        Nothing on this page sends anything.
      </p>
      <TruncatedNote completeFrom={data.truncated ? data.completeFrom : null} />
      <OtherCurrencyNote totals={data.totals} />
      <Card title="By week" subtitle="Weeks start on Monday">
        <WeeksTable summary={data} />
      </Card>
      <Card title="Why the rest cannot be recovered" subtitle={`All ${data.weeks.length} weeks`}>
        <ReasonsTable summary={data} />
      </Card>
    </>
  )
}

function TruncatedNote({ completeFrom }: { completeFrom: string | null }) {
  if (!completeFrom) return null
  const from = new Date(completeFrom).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <p className="rec-note rec-note--warning" role="note">
      There were more unfinished checkouts in this window than one report reads. The figures are complete from {from}{' '}
      onwards; earlier weeks may be undercounted. Choose fewer weeks for a complete view.
    </p>
  )
}

function OtherCurrencyNote({ totals }: { totals: RecoveryWeek }) {
  const other = totals.eligible.otherCurrencyCount + totals.ineligible.otherCurrencyCount
  if (other === 0) return null
  return (
    <p className="rec-note" role="note">
      {formatInteger(other)} lines were in another currency. They are counted but not included in the totals.
    </p>
  )
}

function WeeksTable({ summary }: { summary: RecoverableCheckoutsSummary }) {
  const { currency } = summary
  const columns: Column<RecoveryWeek>[] = [
    { key: 'week', header: 'Week of', cell: (week) => weekLabel(week.weekStart), sortValue: (week) => week.weekStart },
    {
      key: 'unfinished',
      header: 'Unfinished',
      align: 'end',
      cell: (week) => formatInteger(week.eligible.count + week.ineligible.count),
      sortValue: (week) => week.eligible.count + week.ineligible.count,
    },
    {
      key: 'unfinishedValue',
      header: 'Unfinished value',
      align: 'end',
      cell: (week) => money(week.eligible.valueMinor + week.ineligible.valueMinor, currency),
      sortValue: (week) => week.eligible.valueMinor + week.ineligible.valueMinor,
    },
    {
      key: 'recoverable',
      header: 'Recoverable',
      align: 'end',
      cell: (week) => formatInteger(week.eligible.count),
      sortValue: (week) => week.eligible.count,
    },
    {
      key: 'recoverableValue',
      header: 'Recoverable value',
      align: 'end',
      cell: (week) => money(week.eligible.valueMinor, currency),
      sortValue: (week) => week.eligible.valueMinor,
    },
  ]
  return (
    <DataTable
      caption="Unfinished and recoverable checkouts by week"
      columns={columns}
      rows={[...summary.weeks].reverse()}
      rowKey={(week) => week.weekStart}
    />
  )
}

interface ReasonRow {
  reason: RecoveryReason
  tally: RecoveryTally
}

function ReasonsTable({ summary }: { summary: RecoverableCheckoutsSummary }) {
  const rows: ReasonRow[] = [
    { reason: 'eligible', tally: summary.totals.eligible },
    ...INELIGIBLE_REASONS.map((reason) => ({ reason, tally: summary.totals.byReason[reason] })),
  ]
  const columns: Column<ReasonRow>[] = [
    {
      key: 'reason',
      header: 'Reason',
      cell: (row) => <Badge tone={REASON_TONES[row.reason]}>{REASON_LABELS[row.reason]}</Badge>,
    },
    { key: 'lines', header: 'Lines', align: 'end', cell: (row) => formatInteger(row.tally.count) },
    { key: 'value', header: 'Value', align: 'end', cell: (row) => money(row.tally.valueMinor, summary.currency) },
  ]
  return (
    <DataTable
      caption="Unfinished checkouts by reason"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.reason}
    />
  )
}

function LinesCard({
  weeks,
  environments,
  reason,
  onReason,
  currency,
}: {
  weeks: number
  environments: readonly Environment[]
  reason: RecoveryReason | undefined
  onReason: (reason: RecoveryReason | undefined) => void
  currency: string
}) {
  const { can } = usePermissions()
  const [limit, setLimit] = useState(PAGE_SIZE)
  const query: RecoveryQuery = { weeks, environments, reason, limit, offset: 0 }
  const list = useRecoveryList(query)

  const columns: Column<RecoverableCheckoutLine>[] = [
    {
      key: 'failedAt',
      header: 'Failed',
      cell: (line) =>
        new Date(line.failedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
    },
    { key: 'order', header: 'Order', cell: (line) => `#${line.externalOrderId}` },
    { key: 'competition', header: 'Competition', cell: (line) => line.competitionName ?? line.externalCompetitionId ?? '—' },
    { key: 'status', header: 'Status', cell: (line) => line.status },
    { key: 'customer', header: 'Customer', cell: (line) => (line.externalCustomerId ? `#${line.externalCustomerId}` : 'Guest') },
    { key: 'value', header: 'Value', align: 'end', cell: (line) => money(line.valueMinor, line.currency || currency) },
    {
      key: 'reason',
      header: 'Reason',
      cell: (line) => <Badge tone={REASON_TONES[line.reason]}>{REASON_LABELS[line.reason]}</Badge>,
    },
  ]

  return (
    <Card
      title="Unfinished checkouts"
      subtitle="Newest first. Order and customer numbers are the shop's own."
      actions={can('exports.create') ? <CsvButton query={{ weeks, environments, reason }} /> : null}
    >
      <label className="ci-field ci-field--inline rec-filter">
        <span>Reason</span>
        <select
          value={reason ?? ''}
          onChange={(event) => {
            setLimit(PAGE_SIZE)
            onReason(event.target.value ? (event.target.value as RecoveryReason) : undefined)
          }}
        >
          <option value="">All reasons</option>
          {RECOVERY_REASONS.map((option) => (
            <option key={option} value={option}>
              {REASON_LABELS[option]}
            </option>
          ))}
        </select>
      </label>
      {list.isPending ? (
        <LoadingBlock rows={5} label="Loading unfinished checkouts" />
      ) : !list.data ? (
        <EmptyState variant="outage" compact announce />
      ) : (
        <>
          <DataTable
            caption="Unfinished checkouts"
            columns={columns}
            rows={list.data.rows}
            rowKey={(line) => `${line.orderId}:${line.competitionId ?? line.externalCompetitionId}`}
            empty={<EmptyState variant="no-results" compact />}
          />
          <p className="rec-note">
            Showing {formatInteger(list.data.rows.length)} of {formatInteger(list.data.total)}
          </p>
          {list.data.rows.length < list.data.total ? (
            <button
              type="button"
              className="ci-button"
              disabled={list.isFetching}
              onClick={() => setLimit((current) => current + PAGE_SIZE)}
            >
              {list.isFetching ? 'Loading more…' : 'Show more'}
            </button>
          ) : null}
        </>
      )}
    </Card>
  )
}

function CsvButton({ query }: { query: RecoveryQuery }) {
  const client = useApiClient()
  const download = useMutation({
    mutationFn: () => client.recoverableCheckoutsCsv(query),
    onSuccess: (blob) => saveBlob(blob, `recoverable-checkouts-${query.weeks}-weeks.csv`),
  })
  return (
    <>
      <button type="button" className="ci-button" disabled={download.isPending} onClick={() => download.mutate()}>
        {download.isPending ? 'Preparing CSV…' : 'Download CSV'}
      </button>
      {download.isError ? (
        <p role="alert" className="rec-error">
          The CSV could not be prepared just now. Try again in a moment.
        </p>
      ) : null}
    </>
  )
}
